import React, { useEffect, useState } from 'react';
import { Container, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PageHeader from 'components/PageHeader';
import {
  getNaceEndringer,
  getNaceKodeInfo,
  NaceHovedkategoriEndring,
} from 'services/NaceEndringer';

const NaceEndringerPage: React.FC = () => {
  type EnrichedEndring = NaceHovedkategoriEndring & {
    gammelNaceKodeErGammelVersjon?: boolean;
    gammelNaceKodeNotes?: string | null;
    nyNaceKodeErGammelVersjon?: boolean;
    nyNaceKodeNotes?: string | null;
  };
  const [items, setItems] = useState<EnrichedEndring[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getNaceEndringer();

      // For records missing old description, look it up from Admin API
      const enriched = await Promise.all(
        data.map(async (item) => {
          const [gammelInfo, nyInfo] = await Promise.all([
            getNaceKodeInfo(item.gammelNaceKode),
            getNaceKodeInfo(item.nyNaceKode),
          ]);
          return {
            ...item,
            gammelNaceKodeBeskrivelse:
              item.gammelNaceKodeBeskrivelse ?? gammelInfo?.shortName ?? null,
            gammelNaceKodeErGammelVersjon:
              gammelInfo !== null ? !gammelInfo.isCurrentVersion : undefined,
            gammelNaceKodeNotes: gammelInfo?.notes ?? null,
            nyNaceKodeBeskrivelse: item.nyNaceKodeBeskrivelse ?? nyInfo?.shortName ?? null,
            nyNaceKodeErGammelVersjon: nyInfo !== null ? !nyInfo.isCurrentVersion : undefined,
            nyNaceKodeNotes: nyInfo?.notes ?? null,
          };
        }),
      );

      setItems(
        enriched
          .filter((item) => !item.gammelNaceKodeErGammelVersjon && !item.nyNaceKodeErGammelVersjon)
          .sort((a, b) => new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()),
      );
    } catch (e: any) {
      setError(e?.message || 'Ukjent feil ved henting av NACE-endringer.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const formatDate = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('nb-NO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <Container fluid className="mt-3">
      <PageHeader>Brreg NACE-kategoriendringer</PageHeader>

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {error && !loading && <div className="alert alert-warning">{error}</div>}

      {!loading && !error && items.length === 0 && (
        <p className="text-muted">Ingen registrerte NACE-kategoriendringer.</p>
      )}

      {!loading && items.length > 0 && (
        <Table striped bordered hover responsive size="sm">
          <thead className="table-dark">
            <tr>
              <th>OrgNr</th>
              <th>Juridisk navn</th>
              <th>Gammel kategori</th>
              <th>Ny kategori</th>
              <th>Oppdaget</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>
                  <Link to={`/enheter?criteria=${item.orgNr}`}>{item.orgNr}</Link>
                </td>
                <td>
                  <Link to={`/enheter?criteria=${item.orgNr}`}>{item.juridiskNavn ?? '—'}</Link>
                </td>
                <td>
                  <strong
                    title={item.gammelNaceKodeNotes ?? undefined}
                    style={
                      item.gammelNaceKodeNotes
                        ? { cursor: 'help', textDecoration: 'underline dotted' }
                        : undefined
                    }
                  >
                    {item.gammelNaceKode}
                  </strong>
                  {item.gammelNaceKodeBeskrivelse && (
                    <span className="text-muted ms-1">– {item.gammelNaceKodeBeskrivelse}</span>
                  )}
                  {item.gammelNaceKodeErGammelVersjon && (
                    <span
                      className="badge bg-warning text-dark ms-1"
                      title="Koden tilhører en eldre versjon av NACE-registeret"
                    >
                      Eldre versjon
                    </span>
                  )}
                </td>
                <td>
                  <strong
                    title={item.nyNaceKodeNotes ?? undefined}
                    style={
                      item.nyNaceKodeNotes
                        ? { cursor: 'help', textDecoration: 'underline dotted' }
                        : undefined
                    }
                  >
                    {item.nyNaceKode}
                  </strong>
                  {item.nyNaceKodeBeskrivelse && (
                    <span className="text-muted ms-1">– {item.nyNaceKodeBeskrivelse}</span>
                  )}
                </td>
                <td>{formatDate(item.detectedAt)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default NaceEndringerPage;
